import React, { useCallback, useEffect, useRef } from 'react';
import H from '@here/maps-api-for-javascript';
import { useDispatch } from 'react-redux';
import withTranslation from '../../../hoc/withTranslation';
import { enhanceActivityGeoJSONFeatures } from './utils/activityMarkerHelpers';
import { updateActivityGroupMarkers } from './utils/activityMarkerFactory';
import { attachActivityHoverEvents } from './hooks/useActivityMarkerEvents';
import { fitBoundsWithOffset } from '../NewJobMarkers';
import { areMarkerPropsEqual } from '../utils/markerPropsUtils';

const NewActivityMarkers = ({ translations, map, geoJSON, ui, user, display }) => {
  const dispatch = useDispatch();
  const groupRef = useRef(null);
  const translationsRef = useRef(translations);
  const displayRef = useRef(display);
  const hasInitializedBoundsRef = useRef(false);

  useEffect(() => {
    translationsRef.current = translations;
    displayRef.current = display;
  });

  const clearGroup = useCallback(() => {
    if (!groupRef.current) return;
    groupRef.current.removeAll();
    hasInitializedBoundsRef.current = false;
  }, []);

  useEffect(() => {
    if (map && !groupRef.current) {
      groupRef.current = new H.map.Group();
      map.addObject(groupRef.current);
    }
    return () => {
      if (groupRef.current) {
        groupRef.current.removeAll();
        map.removeObject(groupRef.current);
        groupRef.current = null;
      }
    };
  }, [map]);

  useEffect(() => {
    if (!groupRef.current) return;

    if (display?.showProblem) {
      clearGroup();
    }
  }, [display?.showProblem, clearGroup]);

  useEffect(() => {
    if (!groupRef.current || !ui.current) return;

    if (!geoJSON?.geo?.features || geoJSON.geo.features.length === 0) {
      clearGroup();
      return;
    }

    if (display?.showProblem) {
      clearGroup();
      return;
    }

    if (!user?.mapSettings?.showStops) {
      clearGroup();
      return;
    }

    const showOnlyFirstVisits = user?.mapSettings?.showOnlyFirstVisits || false;
    const enhancedGeoJSON = enhanceActivityGeoJSONFeatures(
      geoJSON,
      displayRef.current,
      showOnlyFirstVisits,
    );

    if (!enhancedGeoJSON?.geo?.features || enhancedGeoJSON.geo.features.length === 0) {
      clearGroup();
      return;
    }

    updateActivityGroupMarkers(
      enhancedGeoJSON,
      groupRef.current,
      translationsRef,
      map,
      attachActivityHoverEvents,
      display?.highlightStop,
      display?.selectedTour,
      dispatch,
      displayRef,
    );

    if (groupRef.current.getObjects().length > 0 && !hasInitializedBoundsRef.current) {
      fitBoundsWithOffset(map, groupRef.current.getBoundingBox());
      hasInitializedBoundsRef.current = true;
    }
  }, [geoJSON, map, translations, ui, display, user, clearGroup]);

  return null;
};

const MemoizedNewActivityMarkers = React.memo(NewActivityMarkers, areMarkerPropsEqual);

export default withTranslation(MemoizedNewActivityMarkers);
